/**
 * Utilidades para la vista previa **Procesar a SAP**: mapeo de detalle API OV
 * a filas estilo carga mayorista y agrupación por orden.
 */
import {
  IX_COMERCIO_PROFILE_ID,
  IX_COMERCIO_PERU_PROFILE_ID,
  MULTIMARCA_PROFILE_ID,
  MULTIMARCA_PERU_PROFILE_ID,
  getBulkFormatCurrency,
  parseMoneyNumber,
  roundMoneyForCurrency
} from './salesOrderBulkPreview';
import { SALES_ORDER_CHANNEL_UI_TO_API } from '../services/salesOrdersList';
import {
  getSalesOrderDetailLineNumericFields,
  pickSalesOrderDetailLineDescription,
  pickSalesOrderDetailLineItemId,
  pickSalesOrderDetailLineSkuForSapPreview,
  pickSalesOrderDetailLinesArray
} from '../services/salesOrdersList';
import {
  isDockersBrandName,
  isPeruFederalTaxIdForWholesale,
  resolveBulkBrandDiscount
} from '../services/sapB1BusinessPartners';
import { formatSalesOrderDisplayDateEsCl } from './salesOrderListDateDisplay';

/**
 * @param {string|number|undefined|null} value
 * @returns {string}
 */
function shortDateForPreview(value) {
  return formatSalesOrderDisplayDateEsCl(value, '—');
}

/**
 * Fecha OC a mostrar (mismos candidatos que en listado/detalle en lo posible).
 * @param {object} api
 * @returns {string}
 */
function pickOrderFechaOcForPreview(api) {
  if (!api || typeof api !== 'object') return '—';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const v =
    api.shippedDate ??
    api.shipped_date ??
    header?.shippedDate ??
    api.purchaseOrderDate ??
    api.purchase_order_date ??
    api.orderDate ??
    api.order_date ??
    api.fechaOc ??
    api.fecha_oc ??
    api.createdAt ??
    api.created_at;
  return shortDateForPreview(v);
}

/**
 * Dirección / sucursal de entrega desde detalle OV (MULTIMARCA: `shipping_address`).
 * @param {object} api
 * @returns {string}
 */
function pickShippingAddressForPreview(api) {
  if (!api || typeof api !== 'object') return '';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;
  const shippingBlock = api.shipping && typeof api.shipping === 'object' ? api.shipping : null;
  return String(
    api.shippingAddress ??
      api.shipping_address ??
      header?.shippingAddress ??
      header?.shipping_address ??
      meta?.shippingAddress ??
      meta?.shipping_address ??
      (shippingBlock
        ? shippingBlock.address ??
          shippingBlock.shippingAddress ??
          shippingBlock.shipping_address
        : '') ??
      ''
  ).trim();
}

/**
 * Sucursal / nombre mostrado como en listado (Multimarca: `shippingAddress`, no razón social).
 * @param {object} api
 * @returns {string}
 */
function pickOrderSucursalForPreview(api) {
  if (!api || typeof api !== 'object') return '';
  if (isMultimarcaOrderRaw(api)) {
    const ship = pickShippingAddressForPreview(api);
    if (ship) return ship;
  }
  return String(
    api.clientName ??
      api.client_name ??
      api.customerName ??
      api.customer_name ??
      api.client?.companyName ??
      api.client?.company_name ??
      ''
  ).trim();
}

/**
 * Canal de venta desde detalle OV crudo (alineado con listado / `mapSalesOrderDetailApiToUi`).
 * @param {object} api
 * @returns {string}
 */
function pickOrderChannelRaw(api) {
  if (!api || typeof api !== 'object') return '';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;
  const v =
    api.channel ??
    api.salesChannel ??
    api.sales_channel ??
    header?.channel ??
    meta?.channel ??
    '';
  return String(v).trim();
}

/**
 * Marketplace desde detalle OV crudo (alineado con `mapSalesOrderDetailApiToUi` / listado).
 * @param {object} api
 * @returns {string}
 */
function pickOrderMarketplaceRaw(api) {
  if (!api || typeof api !== 'object') return '';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;
  const v =
    api.marketplace ??
    api.marketPlace ??
    header?.marketplace ??
    header?.marketPlace ??
    meta?.marketplace ??
    meta?.marketPlace ??
    '';
  return String(v).trim();
}

/**
 * @param {string} raw
 * @returns {string}
 */
function normalizeIxComercioKey(raw) {
  return String(raw)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
}

/**
 * OV Ix Comercio (canal **o** marketplace, p. ej. columna IXCOMERCIO) → validación SAP con lista **3**.
 * @param {object} api
 * @returns {boolean}
 */
function isIxComercioOrderRaw(api) {
  const expected = normalizeIxComercioKey(
    String(SALES_ORDER_CHANNEL_UI_TO_API.ixcomercio ?? 'IxComercio')
  );
  const matches = (raw) => {
    if (!raw) return false;
    const norm = normalizeIxComercioKey(raw);
    return norm === expected || norm === 'ixcomercio';
  };
  return matches(pickOrderChannelRaw(api)) || matches(pickOrderMarketplaceRaw(api));
}

/**
 * OV IX Comercio Perú (marketplace IXCOMERCIOPERU). Se evalúa antes que `isIxComercioOrderRaw`
 * porque su canal puede ser el mismo «IxComercio» que Chile y debe usar el perfil en soles.
 * @param {object} api
 * @returns {boolean}
 */
function isIxComercioPeruOrderRaw(api) {
  const mp = pickOrderMarketplaceRaw(api);
  return Boolean(mp) && normalizeIxComercioKey(mp) === 'ixcomercioperu';
}

/**
 * OV Multimarca (marketplace/canal MULTIMARCA), en cualquiera de sus dos países.
 * @param {object} api
 * @returns {boolean}
 */
function isMultimarcaOrderRaw(api) {
  const matches = (raw) => {
    if (!raw) return false;
    const norm = normalizeIxComercioKey(raw);
    return norm === 'multimarca' || norm === 'multimarcaperu';
  };
  return matches(pickOrderChannelRaw(api)) || matches(pickOrderMarketplaceRaw(api));
}

/**
 * OV Multimarca Perú (marketplace MULTIMARCAPERU): mismo criterio que IX Comercio Perú,
 * se evalúa antes que la variante chilena para usar el perfil en soles.
 * @param {object} api
 * @returns {boolean}
 */
function isMultimarcaPeruOrderRaw(api) {
  const mp = pickOrderMarketplaceRaw(api);
  return Boolean(mp) && normalizeIxComercioKey(mp) === 'multimarcaperu';
}

/**
 * Porcentaje de descuento de cabecera OV (p. ej. `discount_amount` en BD / SAP DiscountPercent).
 * Alineado con `mapSalesOrderDetailApiToUi` (`finance.discountAmount` como alias de %).
 *
 * @param {object} api - cuerpo crudo detalle OV
 * @returns {number} 0 si no hay valor usable
 */
export function pickOrderHeaderDiscountPercent(api) {
  if (!api || typeof api !== 'object') return 0;
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const financeRaw = api.finance && typeof api.finance === 'object' ? api.finance : {};
  const rawDiscountPercent =
    financeRaw.discountPercent ??
    financeRaw.discount_percent ??
    api.discountPercent ??
    api.discount_percent ??
    financeRaw.discountAmount ??
    financeRaw.discount_amount ??
    api.discountAmount ??
    api.discount_amount ??
    header?.discountPercent ??
    header?.discount_percent ??
    header?.discountAmount ??
    header?.discount_amount;
  if (rawDiscountPercent == null || rawDiscountPercent === '') return 0;
  const n =
    typeof rawDiscountPercent === 'number' && Number.isFinite(rawDiscountPercent)
      ? rawDiscountPercent
      : parseFloat(String(rawDiscountPercent).replace(/\s/g, '').replace(',', '.'));
  if (Number.isFinite(n) && n >= 0) return n;
  return 0;
}

/**
 * RUT / identificador fiscal del cliente de la OV (mismos alias que `mapSalesOrderDetailApiToUi`).
 *
 * @param {object} api - cuerpo crudo detalle OV
 * @returns {string} RUT en texto (vacío si no hay)
 */
export function pickOrderCustomerRutForSap(api) {
  if (!api || typeof api !== 'object') return '';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;
  const clientRaw = api.client ?? api.customer ?? {};
  const rut =
    clientRaw.rut ??
    clientRaw.taxId ??
    clientRaw.tax_id ??
    api.clientRut ??
    api.client_rut ??
    api.customerRut ??
    api.customer_rut ??
    header?.clientRut ??
    header?.client_rut ??
    meta?.clientRut ??
    '';
  return String(rut ?? '').trim();
}

/** Valores del select «Tipo de venta»: no son marca comercial. */
const WHOLESALE_SALE_TYPE_LABELS = new Set(['venta', 'consignacion', 'traslado']);

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparableLabel(value) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
function isWholesaleSaleTypeLabel(raw) {
  return WHOLESALE_SALE_TYPE_LABELS.has(normalizeComparableLabel(raw));
}

/**
 * Marca comercial de la OV desde el detalle crudo (campos explícitos de marca, no tipo de venta).
 * Omite `Venta`, `Consignación` y `Traslado` si el API los dejó en el campo de marca.
 *
 * @param {object} api - cuerpo crudo detalle OV
 * @returns {string}
 */
export function pickOrderBrandForSap(api) {
  if (!api || typeof api !== 'object') return '';
  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;
  const candidates = [
    api.brand,
    api.Brand,
    api.brandName,
    api.brand_name,
    api.productBrand,
    api.product_brand,
    api.marcaNombre,
    api.marca_nombre,
    header?.brand,
    header?.Brand,
    header?.brandName,
    meta?.brand,
    meta?.Brand,
    meta?.brandName
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (!s || isWholesaleSaleTypeLabel(s)) continue;
    return s;
  }
  return '';
}

/**
 * `true` si la OV corresponde a la marca DOCKERS (para gatillar el selector Hombre/Mujer en envío a SAP).
 *
 * @param {object} api - cuerpo crudo detalle OV
 * @returns {boolean}
 */
export function isDockersOrderRaw(api) {
  return isDockersBrandName(pickOrderBrandForSap(api));
}

/**
 * Construye filas compatibles con `enrichBulkPreviewRowsWithSapItems` desde la respuesta cruda de detalle OV.
 *
 * @param {object} api - cuerpo crudo `GET sales-orders/order-number/...`
 * @returns {{ orderNumber: string, sucursalLabel: string, customerRut: string, isDockers: boolean, isIxComercioPeru: boolean, channel: string, brand: string, rows: object[] }}
 */
export function buildSapProcessPreviewRowsFromOrderRaw(api) {
  const orderNumber = String(api.orderNumber ?? api.order_number ?? api.id ?? '').trim();
  const orderIdRaw = api.id ?? api.salesOrderId ?? api.orderId ?? api.order_id;
  const _orderId =
    orderIdRaw != null && String(orderIdRaw).trim() !== '' ? String(orderIdRaw).trim() : '';
  const sucursalLabel = pickOrderSucursalForPreview(api);
  const fechaOcDisplay = pickOrderFechaOcForPreview(api);

  const bulkProfileForSap = isMultimarcaPeruOrderRaw(api)
    ? MULTIMARCA_PERU_PROFILE_ID
    : isMultimarcaOrderRaw(api)
      ? MULTIMARCA_PROFILE_ID
      : isIxComercioPeruOrderRaw(api)
        ? IX_COMERCIO_PERU_PROFILE_ID
        : isIxComercioOrderRaw(api)
          ? IX_COMERCIO_PROFILE_ID
          : undefined;
  /** Moneda del perfil (CLP redondea a entero; soles conserva 2 decimales en `totalLinea`). */
  const bulkCurrencyCode = getBulkFormatCurrency(bulkProfileForSap).code;

  const headerDiscountPercent = pickOrderHeaderDiscountPercent(api);
  const customerRut = pickOrderCustomerRutForSap(api);
  const brand = pickOrderBrandForSap(api);
  const isDockers = isDockersBrandName(brand);
  const channel = String(api.channel ?? api.salesChannel ?? api.sales_channel ?? '').trim();

  const linesRaw = pickSalesOrderDetailLinesArray(api);
  const rows = linesRaw.map((l, idx) => {
    const skuRaw = pickSalesOrderDetailLineSkuForSapPreview(l);
    const skuIac = skuRaw === '—' ? '' : String(skuRaw).trim();
    const { quantity, unitPrice, lineTotal } = getSalesOrderDetailLineNumericFields(l);
    let precioAcordado =
      unitPrice != null && Number.isFinite(unitPrice) ? unitPrice : null;
    if (precioAcordado == null && lineTotal != null && quantity > 0) {
      precioAcordado = lineTotal / quantity;
    }
    const cantOc =
      typeof quantity === 'number' && Number.isFinite(quantity)
        ? quantity
        : parseMoneyNumber(String(quantity ?? '')) ?? 0;
    const totalLinea =
      lineTotal != null && Number.isFinite(lineTotal)
        ? roundMoneyForCurrency(lineTotal, bulkCurrencyCode)
        : cantOc != null && precioAcordado != null && Number.isFinite(precioAcordado)
          ? roundMoneyForCurrency(cantOc * precioAcordado, bulkCurrencyCode)
          : null;

    const lineItemId = pickSalesOrderDetailLineItemId(l);

    return {
      id: `sap-ov-row-${orderNumber}-${idx + 1}`,
      rowIndex: idx + 1,
      nOc: orderNumber,
      tiendaDestino: sucursalLabel,
      skuIac,
      descripcion: pickSalesOrderDetailLineDescription(l),
      cantOc,
      fechaOc: fechaOcDisplay,
      precioAcordado,
      totalLinea,
      precioListaSap: null,
      stockDispSap: null,
      validationStatus: 'ok',
      validationMessage: '',
      sapPriceMismatch: false,
      _orderNumber: orderNumber,
      _headerDiscountPercent: headerDiscountPercent,
      _customerRut: customerRut,
      ...(_orderId ? { _orderId } : {}),
      ...(lineItemId ? { _itemId: lineItemId } : {}),
      ...(bulkProfileForSap ? { _bulkProfileId: bulkProfileForSap } : {})
    };
  });

  /** IX Comercio Perú no tiene descuento de negocio (un 0 % DOCKERS no es error). */
  const isIxComercioPeru = isIxComercioPeruOrderRaw(api);

  return { orderNumber, sucursalLabel, customerRut, isDockers, isIxComercioPeru, channel, brand, rows };
}

/**
 * Si la cabecera de la OV es 0, copia el descuento de marca del socio (la misma regla que la carga).
 * No estampa Perú + DOCKERS (lo resuelve el selector Hombre/Mujer) ni IX Comercio Perú.
 *
 * @param {Array<{ orderNumber?: string, customerRut?: string, isDockers?: boolean, isIxComercioPeru?: boolean, brand?: string, rows?: object[] }>} builtOrders
 * @param {Map<string, object|null>|null|undefined} partnersByRut
 * @param {{ sessionCountry?: 'PE'|'CL'|null }} [options]
 * @returns {object[]} filas planas; `_headerDiscountPercent` queda con el % de marca cuando aplica
 */
export function applyBrandDiscountFallbackToSapProcessRows(builtOrders, partnersByRut, options = {}) {
  const sessionCountry = options.sessionCountry ?? null;
  const partners = partnersByRut instanceof Map ? partnersByRut : new Map();
  const flat = [];

  for (const built of builtOrders || []) {
    const rows = Array.isArray(built?.rows) ? built.rows : [];
    const rut = String(built?.customerRut || '').trim();
    const isPeru =
      sessionCountry === 'PE' ||
      (sessionCountry !== 'CL' && isPeruFederalTaxIdForWholesale(rut));
    const skipDockersPeru = Boolean(built?.isDockers) && isPeru;
    const skipIxPeru = Boolean(built?.isIxComercioPeru);

    let brandPct = null;
    if (!skipDockersPeru && !skipIxPeru) {
      const partner = rut ? partners.get(rut) : null;
      const resolved = resolveBulkBrandDiscount(partner, built?.brand, { isPeruContext: isPeru });
      if (Number.isFinite(resolved) && resolved > 0) brandPct = resolved;
    }

    for (const row of rows) {
      const headerPct = Number(row?._headerDiscountPercent);
      if (Number.isFinite(headerPct) && headerPct > 0) {
        flat.push(row);
      } else if (brandPct != null) {
        flat.push({ ...row, _headerDiscountPercent: brandPct });
      } else {
        flat.push(row);
      }
    }
  }

  return flat;
}

/**
 * Arma grupos para la tabla (misma forma resumida que `groupBulkPreviewRowsByClient`).
 *
 * @param {Array<{ orderNumber: string, sucursalLabel: string, detailRows: object[] }>} orders
 * @returns {object[]}
 */
export function groupSapProcessPreviewRowsByOrder(orders) {
  if (!Array.isArray(orders) || !orders.length) return [];

  return orders.map((od) => {
    const detailRows = od.detailRows || [];
    const detailCount = detailRows.length;
    let errorLineCount = 0;
    let warningLineCount = 0;
    for (const r of detailRows) {
      if (r.validationStatus === 'error') errorLineCount += 1;
      else if (r.validationStatus === 'warning') warningLineCount += 1;
    }

    let validationStatus = 'ok';
    let validationMessage = '';

    for (const r of detailRows) {
      if (r.validationStatus === 'error') {
        validationStatus = 'error';
        validationMessage = r.validationMessage;
        break;
      }
    }
    if (validationStatus !== 'error') {
      for (const r of detailRows) {
        if (r.validationStatus === 'warning') {
          validationStatus = 'warning';
          validationMessage = r.validationMessage;
          break;
        }
      }
    }
    if (validationStatus === 'ok') {
      validationMessage = detailRows[0]?.validationMessage || '';
    }

    const orderNumber = od.orderNumber ?? '';
    let fechaOc = '';
    for (const r of detailRows) {
      if (r.fechaOc) {
        fechaOc = String(r.fechaOc);
        break;
      }
    }
    return {
      id: `sap-grp-${orderNumber}`,
      clientKey: orderNumber,
      clientLabel: od.sucursalLabel || orderNumber,
      detailCount,
      nOc: orderNumber,
      fechaOc,
      detailRows,
      validationStatus,
      validationMessage,
      warningLineCount,
      errorLineCount
    };
  });
}
